import axios from 'axios';
import { Assessment, Standard, BulkUpdateRequest, AssessmentFilters, SecurityGroup, User, Staff, SubjectHead } from '../types';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Near-static reference data (subject codes, year levels, faculties) is
// otherwise refetched from scratch every time AssessmentList or
// StandardsList mounts, even though it rarely changes. A short in-memory
// cache avoids the redundant round-trip while still picking up real
// changes (e.g. a new subject code) within a few minutes.
const REFERENCE_DATA_TTL_MS = 5 * 60 * 1000;
const referenceDataCache = new Map<string, { data: any; expiresAt: number }>();

const getCached = async (cacheKey: string, path: string) => {
  const cached = referenceDataCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return { data: cached.data };
  }
  const response = await api.get(path);
  referenceDataCache.set(cacheKey, { data: response.data, expiresAt: Date.now() + REFERENCE_DATA_TTL_MS });
  return response;
};

// Auth API
export const authAPI = {
  getAuthUrl: () => api.get('/auth/url'),
  getProfile: () => api.get('/auth/profile'),
  exchangeCode: (code: string) => api.post('/auth/exchange', { code }),
  logout: () => api.post('/auth/logout'),
};

// Assessments API
export const assessmentsAPI = {
  getAssessments: (filters?: AssessmentFilters & { page?: number; limit?: number; sortField?: string; sortDirection?: 'asc' | 'desc' }) =>
    api.get('/assessments', { params: filters }),
  getAssessment: (id: string) => api.get(`/assessments/${id}`),
  createAssessment: (data: Partial<Assessment>) => 
    api.post('/assessments', data),
  updateAssessment: (id: string, data: Partial<Assessment>) => 
    api.put(`/assessments/${id}`, data),
  deleteAssessment: (id: string) => api.delete(`/assessments/${id}`),
  bulkUpdateAssessments: (data: BulkUpdateRequest) => 
    api.post('/assessments/bulk-update', data),
  exportAssessments: () => 
    api.get('/assessments/export', { responseType: 'blob' }),
  exportAssessmentsExcel: () => 
    api.get('/assessments/export-excel', { responseType: 'blob' }),
  generateSubjectPDF: (subjectCode: string) => 
    api.get(`/reports/subject/${subjectCode}.pdf`, { responseType: 'blob' }),
  generateAllSubjectsPDF: () => 
    api.get('/reports/subjects.zip', { responseType: 'blob' }),
  getAssessmentsByDate: () => api.get('/assessments/by-date'),
  exportAssessmentsByDate: () => 
    api.get('/assessments/export-by-date', { responseType: 'blob' }),
  importAssessments: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/assessments/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  getSubjectCodes: () => getCached('subjectCodes', '/assessments/subject-codes'),
  getYearLevels: () => getCached('yearLevels', '/assessments/year-levels'),
  getFaculties: () => getCached('faculties', '/assessments/faculties'),
};

// Standards API
export const standardsAPI = {
  getStandards: (params?: { search?: string; page?: number; limit?: number }) =>
    api.get('/standards', { params }),
  getStandard: (id: string) => api.get(`/standards/${id}`),
  getStandardByCode: (code: string) => api.get(`/standards/code/${code}`),
  createStandard: (data: Partial<Standard>) => 
    api.post('/standards', data),
  updateStandard: (id: string, data: Partial<Standard>) => 
    api.put(`/standards/${id}`, data),
  deleteStandard: (id: string) => api.delete(`/standards/${id}`),
  importStandards: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/standards/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  importStandardsJson: (lines: string[]) => {
    return api.post('/standards/import-json', { lines });
  },
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getAuditLogs: (filters?: { assessment_id?: string; field_name?: string; changed_by?: string; action?: string; limit?: number; page?: number }) => 
    api.get('/dashboard/audit-logs', { params: filters }),
};

// Security Groups API
export const securityGroupsAPI = {
  getGroups: () => api.get('/security-groups'),
  getGroup: (id: string) => api.get(`/security-groups/${id}`),
  createGroup: (data: Partial<SecurityGroup>) => 
    api.post('/security-groups', data),
  updateGroup: (id: string, data: Partial<SecurityGroup>) => 
    api.put(`/security-groups/${id}`, data),
  deleteGroup: (id: string) => api.delete(`/security-groups/${id}`),
};

// User Groups API
export const userGroupsAPI = {
  getUserGroups: () => api.get('/user-groups'),
  getUserGroup: (id: string) => api.get(`/user-groups/${id}`),
  createUserGroup: (data: { userId: string; groupId: string }) => 
    api.post('/user-groups', data),
  deleteUserGroup: (id: string) => api.delete(`/user-groups/${id}`),
};

// Users API
export const usersAPI = {
  getUsers: () => api.get('/users'),
  getUser: (id: string) => api.get(`/users/${id}`),
  updateUser: (id: string, data: Partial<User>) => 
    api.put(`/users/${id}`, data),
};

// Staff & Subject Heads API
export const staffAPI = {
  // Staff endpoints
  getStaff: () => api.get<Staff[]>('/staff'),
  importStaff: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/staff/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Subject Heads endpoints
  getSubjectHeads: () => api.get<SubjectHead[]>('/staff/subject-heads'),
  assignSubjectHead: (data: { subjectCode: string; faculty: string; staffId?: string }) =>
    api.post('/staff/subject-heads/assign', data)
};

export default api;
